import { createSelector } from 'redux-orm';

import orm from '../orm';
import { isLocalId } from '../utils/local-id';
import { selectPath } from './router';
import { selectCurrentUser, selectCurrentUserId } from './users';
import { selectConfig } from './root';
import computeCardPoints, { getLatestArchivedRef } from '../utils/compute-card-points';

const resolveContentCardDefault = (ContentCardDefault, ccdId, pointSystemId) => {
  if (!ccdId) {
    return null;
  }

  const contentCardDefaultModel = ContentCardDefault.withId(ccdId);
  if (
    contentCardDefaultModel &&
    contentCardDefaultModel.pointSistemId === pointSystemId
  ) {
    return contentCardDefaultModel.ref;
  }

  return null;
};

/** Считает баллы карточки с приоритетом архива закрытия. */
const computeListCardPoints = ({
  CardPointData,
  CardPointArchivedData,
  CardPointAdvancedData,
  ContentCardDefault,
  cardId,
  pointSystemId,
  contentViewPlan,
  dampingCoefficients,
}) => {
  const cardPointDataModel = CardPointData.all()
    .filter({ cardId, pointSistemId: pointSystemId })
    .first();

  const cardPointAdvancedDataModels = CardPointAdvancedData.all()
    .filter({ cardId, pointSistemId: pointSystemId })
    .toModelArray();

  if (!cardPointDataModel && cardPointAdvancedDataModels.length === 0) {
    return null;
  }

  const archivedModels = cardPointDataModel
    ? CardPointArchivedData.all()
        .filter({ cpdId: cardPointDataModel.id, pointSistemId: pointSystemId })
        .toModelArray()
    : [];
  const archivedData = getLatestArchivedRef(archivedModels);

  const points = computeCardPoints({
    cardPointData: cardPointDataModel?.ref || null,
    contentCardDefault: resolveContentCardDefault(
      ContentCardDefault,
      cardPointDataModel?.ccdId,
      pointSystemId,
    ),
    cardPointAdvancedData: cardPointAdvancedDataModels.map((model) => model.ref),
    archivedData,
    contentViewPlan,
    dampingCoefficients,
  });

  return {
    ...points,
    cardPointDataModel,
    archivedData,
  };
};export const makeSelectListById = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ List }, id) => {
      const listModel = List.withId(id);

      if (!listModel) {
        return listModel;
      }

      return {
        ...listModel.ref,
        isPersisted: !isLocalId(id),
      };
    },
  );

export const selectListById = makeSelectListById();

export const makeSelectCardIdsByListId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ List }, id) => {
      const listModel = List.withId(id);

      if (!listModel) {
        return listModel;
      }

      return listModel.getFilteredOrderedCardsModelArray().filter(v => v.is_archive != true).map((cardModel) => cardModel.id);
    },
  );

export const selectCardIdsByListId = makeSelectCardIdsByListId();

export const makeSelectArchivedLists = () => 
  createSelector(
    orm,
    (state) => selectPath(state).projectId,
    (state) => selectCurrentUser(state),
    (state) => selectConfig(state),
    ({ List, Board }, projectId, currentUser, config) => {
      let boardsIds = Board.all().filter(v => v.projectId == projectId).toModelArray().map(v => v.id);
      let lists = List.all().filter(v => (v.is_archive == true && boardsIds.indexOf(v.boardId) > -1)).toModelArray();
      
      // Фильтрация по создателю для обычных пользователей
      if (currentUser) {
        const isSAdmin = currentUser.isSAdmin === true;
        const isAdmin = currentUser.isAdmin === true;
        const superuserIds = Array.isArray(config?.superuserIds) ? config.superuserIds : [];
        const isSuperuser = currentUser.id && superuserIds.map(String).includes(String(currentUser.id));
        
        if (!isSAdmin && !isAdmin && !isSuperuser) {
          // Обычные пользователи видят только свои элементы
          lists = lists.filter(v => v.creatorUserId === currentUser.id);
        }
        // Суперпользователи видят все элементы (без фильтрации)
      }
      
      return lists.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((v) => { 
        const date = new Date(v.updatedAt);
        date.setHours(date.getHours() + 3);
        return {...v.ref, updatedAt: date.toISOString().slice(0, 16).replace('T', ' ')} 
      });
    }
  );

export const selectArchivedLists = makeSelectArchivedLists();


export const makeSelectArchivedCards = (state) => 
  createSelector(
    orm,
    (state) => selectPath(state).projectId,
    (state) => selectCurrentUser(state),
    (state) => selectConfig(state),
    ({ Card, Board }, projectId, currentUser, config) => {
      let boardsIds = Board.all().filter(v => v.projectId == projectId).toModelArray().map(v => v.id);
      let cards = Card.all().filter(v => (v.is_archive == true && boardsIds.indexOf(v.boardId) > -1)).toModelArray();
      
      // Фильтрация по создателю для обычных пользователей
      if (currentUser) {
        const isSAdmin = currentUser.isSAdmin === true;
        const isAdmin = currentUser.isAdmin === true;
        const superuserIds = Array.isArray(config?.superuserIds) ? config.superuserIds : [];
        const isSuperuser = currentUser.id && superuserIds.map(String).includes(String(currentUser.id));
        
        if (!isSAdmin && !isAdmin && !isSuperuser) {
          // Обычные пользователи видят только свои элементы
          cards = cards.filter(v => v.creatorUserId === currentUser.id);
        }
        // Суперпользователи видят все элементы (без фильтрации)
      }
      
      return cards.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((v) => { 
        const date = new Date(v.updatedAt);
        date.setHours(date.getHours() + 3);
        return {...v.ref, updatedAt: date.toISOString().slice(0, 16).replace('T', ' ')} 
      });
    }
  );

export const selectArchivedCards = makeSelectArchivedCards();

export const makeSelectArchivedBoards = () => 
  createSelector(
    orm,
    (state) => selectPath(state).projectId,
    (state) => selectCurrentUser(state),
    (state) => selectConfig(state),
    ({ Board }, id, currentUser, config) => {
      let boards = Board.all().filter(v => v.is_archive == true && v.projectId == id).toModelArray();
      
      // Фильтрация по создателю для обычных пользователей
      if (currentUser) {
        const isSAdmin = currentUser.isSAdmin === true;
        const isAdmin = currentUser.isAdmin === true;
        const superuserIds = Array.isArray(config?.superuserIds) ? config.superuserIds : [];
        const isSuperuser = currentUser.id && superuserIds.map(String).includes(String(currentUser.id));
        
        if (!isSAdmin && !isAdmin && !isSuperuser) {
          // Обычные пользователи видят только свои элементы
          boards = boards.filter(v => v.creatorUserId === currentUser.id);
        }
        // Суперпользователи видят все элементы (без фильтрации)
      }
      
      return boards.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((v) => { 
        const date = new Date(v.updatedAt);
        date.setHours(date.getHours() + 3);
        return {...v.ref, updatedAt: date.toISOString().slice(0, 16).replace('T', ' ')} 
      });
    }
  );

export const selectArchivedBoards = makeSelectArchivedBoards();

export const makeSelectListPointsSum = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    (
      {
        List,
        CardPointData,
        CardPointArchivedData,
        CardPointAdvancedData,
        ContentCardDefault,
        ContentViewPlan,
        DampingCoefficient,
        ListReportData,
      },
      listId,
      pointSystemId,
    ) => {
      if (!listId || !pointSystemId) {
        return 0;
      }

      const listReportDataModel = ListReportData.all().filter({ listId }).first();
      const reportYear = listReportDataModel?.year ? Number(listReportDataModel.year) : null;
      const reportMonth = listReportDataModel?.month ? Number(listReportDataModel.month) : null;

      if (!reportYear || !reportMonth) {
        return 0;
      }

      const dampingCoefficientModels = DampingCoefficient.all()
        .filter({ pointSistemId: pointSystemId })
        .toModelArray();

      const dampingCoefficientsHash =
        dampingCoefficientModels.length > 0
          ? `${dampingCoefficientModels.length}:${dampingCoefficientModels
              .map((dc) => `${dc.id}:${dc.from}:${dc.to}:${dc.value}`)
              .sort()
              .join('|')}`
          : '0';

      const listModel = List.withId(listId);
      if (!listModel) {
        return 0;
      }

      const contentViewPlanModels = ContentViewPlan.all()
        .filter({
          pointSistemId: pointSystemId,
          year: reportYear,
          month: reportMonth,
        })
        .toModelArray();
      const contentViewPlanModel = contentViewPlanModels.length > 0 ? contentViewPlanModels[0] : null;
      const contentViewPlan = contentViewPlanModel?.ref || null;
      const contentViewPlanHash = contentViewPlanModel
        ? `${contentViewPlanModel.id}:${contentViewPlanModel.year}:${contentViewPlanModel.month}:${contentViewPlanModel.photo}:${contentViewPlanModel.video}:${contentViewPlanModel.caseCount}:${JSON.stringify(
            contentViewPlanModel.values || {},
          )}`
        : '0';

      const dampingCoefficients = dampingCoefficientModels
        .sort((a, b) => (a.id || 0) - (b.id || 0))
        .map((model) => model.ref);

      const cardModels = listModel
        .getFilteredOrderedCardsModelArray()
        .filter((card) => card.is_archive != true);

      let sum = 0;
      cardModels.forEach((cardModel) => {
        const points = computeListCardPoints({
          CardPointData,
          CardPointArchivedData,
          CardPointAdvancedData,
          ContentCardDefault,
          cardId: cardModel.id,
          pointSystemId,
          contentViewPlan,
          dampingCoefficients,
        });

        if (!points) {
          return;
        }

        sum += points.totalPoints;
      });

      const hashLength = dampingCoefficientsHash.length + contentViewPlanHash.length;
      return sum + hashLength * 0;
    },
  );

export const selectListPointsSum = makeSelectListPointsSum();

export const makeSelectListPointsSumExcludingOtherMonth = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    (
      {
        List,
        CardPointData,
        CardPointArchivedData,
        CardPointAdvancedData,
        ContentCardDefault,
        ContentViewPlan,
        DampingCoefficient,
        ListReportData,
      },
      listId,
      pointSystemId,
    ) => {
      if (!listId || !pointSystemId) {
        return 0;
      }

      const listReportDataModel = ListReportData.all().filter({ listId }).first();
      if (!listReportDataModel || !listReportDataModel.year || !listReportDataModel.month) {
        return 0;
      }

      const reportYear = Number(listReportDataModel.year);
      const reportMonth = Number(listReportDataModel.month);

      const dampingCoefficientModels = DampingCoefficient.all()
        .filter({ pointSistemId: pointSystemId })
        .toModelArray();

      const dampingCoefficientsHash =
        dampingCoefficientModels.length > 0
          ? `${dampingCoefficientModels.length}:${dampingCoefficientModels
              .map((dc) => `${dc.id}:${dc.from}:${dc.to}:${dc.value}`)
              .sort()
              .join('|')}`
          : '0';

      const listModel = List.withId(listId);
      if (!listModel) {
        return 0;
      }

      const contentViewPlanModels = ContentViewPlan.all()
        .filter({
          pointSistemId: pointSystemId,
          year: reportYear,
          month: reportMonth,
        })
        .toModelArray();
      const contentViewPlanModel = contentViewPlanModels.length > 0 ? contentViewPlanModels[0] : null;
      const contentViewPlan = contentViewPlanModel?.ref || null;
      const contentViewPlanHash = contentViewPlanModel
        ? `${contentViewPlanModel.id}:${contentViewPlanModel.year}:${contentViewPlanModel.month}:${contentViewPlanModel.photo}:${contentViewPlanModel.video}:${contentViewPlanModel.caseCount}:${JSON.stringify(
            contentViewPlanModel.values || {},
          )}`
        : '0';

      const dampingCoefficients = dampingCoefficientModels
        .sort((a, b) => (a.id || 0) - (b.id || 0))
        .map((model) => model.ref);

      const cardModels = listModel
        .getFilteredOrderedCardsModelArray()
        .filter((card) => card.is_archive != true);

      let sum = 0;
      cardModels.forEach((cardModel) => {
        const points = computeListCardPoints({
          CardPointData,
          CardPointArchivedData,
          CardPointAdvancedData,
          ContentCardDefault,
          cardId: cardModel.id,
          pointSystemId,
          contentViewPlan,
          dampingCoefficients,
        });

        if (!points?.cardPointDataModel || points.cardPointDataModel.ref?.closed !== true) {
          return;
        }

        const taskClosedAtRaw =
          points.archivedData?.taskClosedAt || points.cardPointDataModel.ref?.closedAt;
        if (!taskClosedAtRaw) {
          return;
        }

        const closedAtDate = new Date(taskClosedAtRaw);
        const closedYear = closedAtDate.getFullYear();
        const closedMonth = closedAtDate.getMonth() + 1;

        // Только задачи, закрытые в другом месяце
        if (closedYear === reportYear && closedMonth === reportMonth) {
          return;
        }

        sum += points.totalPoints;
      });

      const hashLength = dampingCoefficientsHash.length + contentViewPlanHash.length;
      return sum + hashLength * 0;
    },
  );

export const selectListPointsSumExcludingOtherMonth = makeSelectListPointsSumExcludingOtherMonth();

export const makeSelectListClosedPointsSumExcludingOtherMonth = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    (
      {
        List,
        CardPointData,
        CardPointArchivedData,
        CardPointAdvancedData,
        ContentCardDefault,
        ContentViewPlan,
        DampingCoefficient,
        ListReportData,
      },
      listId,
      pointSystemId,
    ) => {
      if (!listId || !pointSystemId) {
        return 0;
      }

      const listReportDataModel = ListReportData.all().filter({ listId }).first();
      if (!listReportDataModel || !listReportDataModel.year || !listReportDataModel.month) {
        return 0;
      }

      const reportYear = Number(listReportDataModel.year);
      const reportMonth = Number(listReportDataModel.month);

      const dampingCoefficientModels = DampingCoefficient.all()
        .filter({ pointSistemId: pointSystemId })
        .toModelArray();

      const dampingCoefficientsHash =
        dampingCoefficientModels.length > 0
          ? `${dampingCoefficientModels.length}:${dampingCoefficientModels
              .map((dc) => `${dc.id}:${dc.from}:${dc.to}:${dc.value}`)
              .sort()
              .join('|')}`
          : '0';

      const listModel = List.withId(listId);
      if (!listModel) {
        return 0;
      }

      const contentViewPlanModels = ContentViewPlan.all()
        .filter({
          pointSistemId: pointSystemId,
          year: reportYear,
          month: reportMonth,
        })
        .toModelArray();
      const contentViewPlanModel = contentViewPlanModels.length > 0 ? contentViewPlanModels[0] : null;
      const contentViewPlan = contentViewPlanModel?.ref || null;
      const contentViewPlanHash = contentViewPlanModel
        ? `${contentViewPlanModel.id}:${contentViewPlanModel.year}:${contentViewPlanModel.month}:${contentViewPlanModel.photo}:${contentViewPlanModel.video}:${contentViewPlanModel.caseCount}:${JSON.stringify(
            contentViewPlanModel.values || {},
          )}`
        : '0';

      const dampingCoefficients = dampingCoefficientModels
        .sort((a, b) => (a.id || 0) - (b.id || 0))
        .map((model) => model.ref);

      const cardModels = listModel
        .getFilteredOrderedCardsModelArray()
        .filter((card) => card.is_archive != true);

      let sum = 0;
      cardModels.forEach((cardModel) => {
        const points = computeListCardPoints({
          CardPointData,
          CardPointArchivedData,
          CardPointAdvancedData,
          ContentCardDefault,
          cardId: cardModel.id,
          pointSystemId,
          contentViewPlan,
          dampingCoefficients,
        });

        if (!points?.cardPointDataModel || points.cardPointDataModel.ref?.closed !== true) {
          return;
        }

        const taskClosedAtRaw =
          points.archivedData?.taskClosedAt || points.cardPointDataModel.ref?.closedAt;
        if (!taskClosedAtRaw) {
          return;
        }

        const closedAtDate = new Date(taskClosedAtRaw);
        const closedYear = closedAtDate.getFullYear();
        const closedMonth = closedAtDate.getMonth() + 1;

        // Только задачи периода отчёта списка
        if (closedYear !== reportYear || closedMonth !== reportMonth) {
          return;
        }

        sum += points.totalPoints;
      });

      const hashLength = dampingCoefficientsHash.length + contentViewPlanHash.length;
      return sum + hashLength * 0;
    },
  );

export const selectListClosedPointsSumExcludingOtherMonth = makeSelectListClosedPointsSumExcludingOtherMonth();

export default {
  makeSelectListById,
  selectListById,
  selectArchivedLists,
  makeSelectArchivedLists,
  selectArchivedCards,
  makeSelectArchivedCards,
  selectArchivedBoards,
  makeSelectArchivedBoards,
  makeSelectCardIdsByListId,
  selectCardIdsByListId,
  makeSelectListPointsSum,
  selectListPointsSum,
  makeSelectListPointsSumExcludingOtherMonth,
  selectListPointsSumExcludingOtherMonth,
  makeSelectListClosedPointsSumExcludingOtherMonth,
  selectListClosedPointsSumExcludingOtherMonth,

};
